import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

// const API_BASE = "http://localhost:9091/api/admin/students";
const API_BASE = "https://api.mediitacademy.com/api/admin/students";

type Enrollment = {
  id: number;
  courseId?: number;
  courseName?: string;
  planName?: string;
  amountPaid?: number;
  transactionId?: string;
  paymentStatus?: string;
  accessEnabled: boolean;
  purchasedAt?: string;
};

type Student = {
  id: number;
  name: string;
  email: string;
  phone: string;
  studentClass?: string;
  accountActive: boolean;
  joinedAt?: string;
  enrollments: Enrollment[];
};

const formatDate = (value?: string) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

const formatAmount = (value?: number | string) => {
  if (!value) return "-";
  return `₹${Number(value).toLocaleString("en-IN")}`;
};

export default function StudentReport() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [student, setStudent] = useState<Student | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStudent = async () => {
      try {
        const adminAuthData = JSON.parse(localStorage.getItem("mediit_admin_auth") || "{}");
        const token = adminAuthData.token || "";
        const response = await fetch(API_BASE, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await response.json();
        const found = data.students?.find((s: Student) => String(s.id) === id);
        setStudent(found || null);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    fetchStudent();
  }, [id]);

  if (loading) return <div className="p-10 text-center font-black text-slate-500">Loading Student Report...</div>;
  if (!student) return <div className="p-10 text-center font-black text-red-500">Student not found</div>;

  const totalPaid = student.enrollments.reduce((sum, e) => sum + Number(e.amountPaid || 0), 0);
  const activeCourses = student.enrollments.filter(e => e.accessEnabled).length;

  return (
    <div className="w-full min-w-0 space-y-6 font-['Inter']">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate(-1)} className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 shadow-sm">
          ←
        </button>
        <div>
          <h1 className="text-2xl font-black text-slate-950">Student 360° Report</h1>
          <p className="text-sm font-bold text-slate-500">Detailed analytics and enrollment history.</p>
        </div>
      </div>

      {/* Header Profile Card */}
      <div className="rounded-[34px] bg-white p-6 sm:p-8 shadow-sm border border-slate-200">
        <div className="flex flex-col sm:flex-row gap-6 justify-between sm:items-center border-b border-slate-100 pb-6">
          <div className="flex items-center gap-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-[20px] bg-slate-950 text-2xl font-black text-white shadow-lg">
              {student.name.charAt(0).toUpperCase()}
            </div>
            <div>
              <h2 className="text-3xl font-black text-slate-950">{student.name}</h2>
              <p className="text-sm font-bold text-slate-500 mt-1">{student.email} • {student.phone}</p>
            </div>
          </div>
          <div className="flex gap-3">
            <span className={`px-4 py-2 rounded-2xl text-sm font-black ${student.accountActive ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
              {student.accountActive ? 'Login: Active' : 'Login: Disabled'}
            </span>
            <span className="px-4 py-2 rounded-2xl bg-blue-50 text-blue-700 text-sm font-black">
              Class: {student.studentClass || 'N/A'}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 pt-6">
          <StatBox label="Total Spent" value={`₹${totalPaid.toLocaleString('en-IN')}`} />
          <StatBox label="Active Courses" value={activeCourses} />
          <StatBox label="Total Enrollments" value={student.enrollments.length} />
          <StatBox label="Joined Date" value={formatDate(student.joinedAt)} smallValue />
        </div>
      </div>

      {/* Enrollments Table */}
      <div className="rounded-[34px] bg-white p-6 sm:p-8 shadow-sm border border-slate-200">
        <h3 className="text-xl font-black text-slate-950 mb-6">Enrollment & Payment History</h3>
        {student.enrollments.length === 0 ? (
          <div className="p-8 text-center bg-slate-50 rounded-2xl border border-dashed border-slate-200">
            <p className="text-sm font-bold text-slate-500">No courses purchased yet.</p>
          </div>
        ) : (
          <div className="w-full overflow-x-auto rounded-2xl border border-slate-100">
            <table className="w-full min-w-[800px] text-left">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50 text-xs font-black uppercase tracking-wide text-slate-400">
                  <th className="px-5 py-4">Course Details</th>
                  <th className="px-5 py-4">Transaction ID</th>
                  <th className="px-5 py-4">Amount</th>
                  <th className="px-5 py-4">Date</th>
                  <th className="px-5 py-4">Payment</th>
                  <th className="px-5 py-4">Access</th>
                </tr>
              </thead>
              <tbody>
                {student.enrollments.map((e) => (
                  <tr key={e.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-5 py-4">
                      <p className="text-sm font-black text-slate-900">{e.courseName || e.planName}</p>
                      <p className="text-xs font-bold text-slate-500">Course ID: #{e.courseId}</p>
                    </td>
                    <td className="px-5 py-4 text-xs font-bold text-slate-600">{e.transactionId || '-'}</td>
                    <td className="px-5 py-4 text-sm font-black text-slate-900">{formatAmount(e.amountPaid)}</td>
                    <td className="px-5 py-4 text-xs font-bold text-slate-500">{formatDate(e.purchasedAt)}</td>
                    <td className="px-5 py-4">
                      <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${e.paymentStatus === 'APPROVED' || e.paymentStatus === 'PAID' ? 'bg-emerald-50 text-emerald-700' : 'bg-orange-50 text-orange-700'}`}>
                        {e.paymentStatus}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${e.accessEnabled ? 'bg-blue-50 text-blue-700' : 'bg-red-50 text-red-700'}`}>
                        {e.accessEnabled ? 'Granted' : 'Revoked'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Activity & Performance Placeholder */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-[34px] bg-white p-6 shadow-sm border border-slate-200">
          <h3 className="text-xl font-black text-slate-950 mb-2">Live Class Attendance</h3>
          <p className="text-sm font-semibold text-slate-500 mb-6">Track student's presence in live sessions.</p>
          <div className="flex flex-col items-center justify-center p-10 bg-[#f8fbff] rounded-2xl border border-dashed border-blue-200">
            <span className="text-4xl mb-3">📊</span>
            <p className="text-sm font-black text-blue-900">Attendance Data Coming Soon</p>
            <p className="text-xs font-bold text-blue-600 mt-1">Connecting to Live Server API...</p>
          </div>
        </div>
        <div className="rounded-[34px] bg-white p-6 shadow-sm border border-slate-200">
          <h3 className="text-xl font-black text-slate-950 mb-2">Mock Test Performance</h3>
          <p className="text-sm font-semibold text-slate-500 mb-6">Subject-wise scores and rankings.</p>
          <div className="flex flex-col items-center justify-center p-10 bg-[#f8fbff] rounded-2xl border border-dashed border-blue-200">
            <span className="text-4xl mb-3">🏆</span>
            <p className="text-sm font-black text-blue-900">Test Scores Coming Soon</p>
            <p className="text-xs font-bold text-blue-600 mt-1">Test Series module under development.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

const StatBox = ({ label, value, smallValue = false }: { label: string, value: any, smallValue?: boolean }) => (
  <div className="p-4 rounded-2xl bg-[#f8fbff] border border-slate-100">
    <p className="text-xs font-black uppercase text-slate-400">{label}</p>
    <p className={`${smallValue ? 'text-sm mt-2' : 'text-2xl mt-1'} font-black text-slate-950`}>{value}</p>
  </div>
);